import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { GitHubButton } from '@/components/auth/GitHubButton';
import { useAuth } from '@/contexts/AuthContext';

interface InviteDetails {
  invite_id: string;
  project_id: string;
  project_name: string;
  invite_email: string | null;
  role: string;
  inviter_name: string | null;
  has_existing_account: boolean;
}

export function InviteLoginPage() {
  const { inviteId, secret } = useParams<{ inviteId: string; secret: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();

  useEffect(() => {
    async function fetchInvite() {
      try {
        const response = await fetch(`/api/invite/${inviteId}/${secret}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          const data = await response.json();
          setInviteError(data.error || 'Invalid invitation link');
          setLoading(false);
          return;
        }

        const data = await response.json();
        setInvite(data);
        if (data.invite_email) {
          setEmail(data.invite_email);
        }

        // If user is already authenticated, redirect to confirm page
        if (data.is_authenticated) {
          window.location.href = `/auth/invite-confirm/${inviteId}/${secret}`;
        }
      } catch {
        setInviteError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    }

    fetchInvite();
  }, [inviteId, secret]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await login({ email, password });

      if (!result.success) {
        setError(result.error || 'An error occurred. Please try again.');
        setIsSubmitting(false);
        return;
      }

      if (result.requires_2fa) {
        // Redirect to 2FA page, then back to invite
        navigate('/auth/verify-2fa');
        return;
      }

      // Success - redirect to accept the invitation
      window.location.href = `/project-invite/${inviteId}/${secret}`;
    } catch {
      setError('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AuthLayout>
    );
  }

  if (inviteError || !invite) {
    return (
      <AuthLayout>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{inviteError || 'Invalid invitation'}</AlertDescription>
        </Alert>
        <div className="text-center">
          <Link to="/auth/login">
            <Button variant="outline">Go to Login</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Invitation Banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">
              You're invited to join <span className="text-primary">{invite.project_name}</span>
            </p>
            {invite.inviter_name && (
              <p className="text-sm text-muted-foreground mt-1">
                Invited by {invite.inviter_name} as {invite.role}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Title */}
      <h1 className="text-xl font-semibold text-gray-900 mb-2">
        Log in to accept invitation
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        We found an existing account for {invite.invite_email}. Please log in to continue.
      </p>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            autoFocus
          />
        </div>

        <div className="text-right">
          <Link
            to="/auth/forgot-password"
            className="text-sm text-primary hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          className="w-full h-11"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Logging in...
            </>
          ) : (
            'Log In & Join Project'
          )}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative my-6">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
          or continue with
        </span>
      </div>

      {/* OAuth Buttons */}
      <div className="space-y-3">
        <GoogleButton text="Sign in with Google" />
        <GitHubButton text="Sign in with GitHub" />
      </div>

      {/* Signup Link */}
      <div className="mt-6 text-center">
        <Link
          to={`/auth/invite/${inviteId}/${secret}`}
          className="text-sm text-muted-foreground hover:text-primary"
        >
          Don't have an account? Create one
        </Link>
      </div>
    </AuthLayout>
  );
}
